import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";

const ACCELERATION_STEP: number = 0.05;
const MIN_DISPLACEMENT: number = 0.01;
const MAX_DISPLACEMENT: number = 1;
const MIN_VELOCITY: number = -1;
const MAX_VELOCITY: number = 1;

/**
 * The `StringGlide` class is a module that handles the glide effect for string objects
 * based on scroll events. It calculates displacement, acceleration, and velocity
 * to create a smooth scrolling effect for objects.
 */
export class StringGlide extends StringModule {
  private previousLerp: number = 0;
  private displacement: number = 0;
  private acceleration: number = 0;
  private velocityMultiplier: number = 0.00125;
  private isInitialScroll: boolean = true;

  private baseVelocityMultiplier: number = 0.00125;
  private reducedVelocityMultiplier: number = this.baseVelocityMultiplier / 20;
  private negativeVelocityMultiplier: number = -0.0001;

  private maxDisplacementValue: number = 0;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "glide";

    this.cssProperties = [
      { name: "--glide", syntax: "<number>", initialValue: "0", inherits: true }
    ];

    this.baseVelocityMultiplier =
      this.settings["glide-base-velocity"] ?? this.baseVelocityMultiplier;
    this.reducedVelocityMultiplier =
      this.settings["glide-reduce-velocity"] ?? this.reducedVelocityMultiplier;
    this.negativeVelocityMultiplier =
      this.settings["glide-negative-velocity"] ?? this.negativeVelocityMultiplier;

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "glide", type: "number", fallback: this.settings["glide"] },
    ];
  }

  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.glide = object.getProperty<number>("glide") ?? 0;
    object.eventGlideName = this.getObjectEventName(object, "object:glide");
    object.setProperty("__next-glide", 0);
    object.setProperty("__next-glide-transform", "translate3d(0, 0px, 0)");
    object.setProperty("__needs-glide-var-update", false);
    object.setProperty("__needs-glide-transform-update", false);
  }

  private setupItem = (object: StringObject) => {
    let glideValue = -this.data.scroll.displacement * this.maxDisplacementValue * object.glide;

    if (object.eventGlideName) {
      this.events.emit(object.eventGlideName, glideValue);
    }

    const transformCompute = `translate3d(0, ${glideValue}px, 0)`;
    object.setProperty("__next-glide-transform", transformCompute);
    object.setProperty("__needs-glide-transform-update", true);
  };

  private onUpdateDesktopEvent = () => {
    for (let i = 0; i < this.objects.length; i++) {
      this.setupItem(this.objects[i]);
    }
  };
  private onUpdateMobileEvent = () => { };
  private onUpdateEvent = this.onUpdateDesktopEvent;

  private calcExpanderFactor(isDirectionUp: boolean): void {
    const isConditionMet = isDirectionUp
      ? this.data.scroll.lerped < this.previousLerp
      : this.data.scroll.lerped > this.previousLerp;

    this.velocityMultiplier = isConditionMet
      ? this.isInitialScroll
        ? this.baseVelocityMultiplier
        : this.reducedVelocityMultiplier
      : this.negativeVelocityMultiplier;

    if (!isConditionMet) {
      this.isInitialScroll = false;
    }
  }
  onStart(): void {
    this.maxDisplacementValue = this.data.viewport.windowHeight * 0.1;
  }

  onResize(): void {
    if (window.innerWidth > 1024) {
      this.maxDisplacementValue = this.data.viewport.windowHeight * 0.1;
      this.onUpdateEvent = this.onUpdateDesktopEvent;
    } else {
      this.onUpdateEvent = this.onUpdateMobileEvent;
      this.resetState();
      for (let i = 0; i < this.objects.length; i++) {
        const object = this.objects[i];
        object.setProperty("__next-glide-transform", "translate3d(0, 0px, 0)");
        object.setProperty("__needs-glide-transform-update", true);
        object.setProperty("__next-glide", 0);
        object.setProperty("__needs-glide-var-update", true);
      }
      this.flushPendingGlideStyles();
    }
  }

  private resetState(): void {
    this.displacement = 0;
    this.acceleration = 0;
    this.isInitialScroll = true;
    this.velocityMultiplier = this.baseVelocityMultiplier;
  }

  onScrollStart(): void {
    this.resetState();
  }

  onScrollStop(): void {
    this.resetState();
    this.previousLerp = 0;
    this.data.scroll.displacement = 0;

    const dispNum = 0;
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      object.setProperty("__next-glide-transform", "translate3d(0, 0px, 0)");
      object.setProperty("__needs-glide-transform-update", true);
      object.setProperty("__next-glide", dispNum);
      object.setProperty("__needs-glide-var-update", true);
    }
    this.flushPendingGlideStyles();
  }

  onFrame(data: StringData): void {
    this.calcExpanderFactor(this.data.scroll.isScrollingDown === false);
    this.acceleration = Math.min(MAX_DISPLACEMENT, this.acceleration + ACCELERATION_STEP);
    this.displacement = Math.max(
      MIN_DISPLACEMENT,
      Math.min(MAX_DISPLACEMENT, this.displacement + this.velocityMultiplier)
    );
    this.data.scroll.displacement = Math.min(
      MAX_VELOCITY,
      Math.max(MIN_VELOCITY, this.data.scroll.lerped * this.displacement * this.acceleration)
    );

    const dispNum = this.data.scroll.displacement;

    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      object.setProperty("__next-glide", dispNum);
      object.setProperty("__needs-glide-var-update", true);
    }
    this.previousLerp = this.data.scroll.lerped;
    this.onUpdateEvent();
  }

  override onMutate(): void {
    this.applyPendingGlideStyles();
  }

  private applyPendingGlideStyles(): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];

      const needsVarUpdate = object.getProperty<boolean>("__needs-glide-var-update") === true;
      const needsTransformUpdate =
        object.getProperty<boolean>("__needs-glide-transform-update") === true;

      if (!needsVarUpdate && !needsTransformUpdate) {
        continue;
      }

      if (needsVarUpdate) {
        const glideValue = object.getProperty<number>("__next-glide") ?? this.data.scroll.displacement;
        this.applyVarToElement(object, "--glide", glideValue);
        this.applyVarToConnects(object, "--glide", glideValue);
        object.setProperty("__needs-glide-var-update", false);
      }

      if (needsTransformUpdate) {
        const transform =
          object.getProperty<string>("__next-glide-transform") ?? "translate3d(0, 0px, 0)";
        this.applyPropToElement(object, "transform", transform);
        object.setProperty("__needs-glide-transform-update", false);
      }
    }
  }

  private flushPendingGlideStyles(): void {
    if (styleTxn.isOpen) {
      this.applyPendingGlideStyles();
      return;
    }
    styleTxn.run(() => {
      this.applyPendingGlideStyles();
    });
  }
}
